/**
 * AgentPersonaRegistry + AgentSessionManager — die Verwaltungsschicht über
 * den Figuren (Alex 2026-08-03: „mehrere Agenten einfach und schnell").
 *
 *   Registry  — kennt alle Figuren des Mandanten, löst per id auf.
 *   Manager   — hält je Figur EINEN Controller (lazy erzeugt) und weiß,
 *               welcher gerade aktiv ist. Beim Wechsel wird die laufende
 *               Sitzung geschlossen: Ein Mikrofon, eine Stimme, ein
 *               Realtime-Kanal — zwei gleichzeitig wären weder technisch
 *               sinnvoll noch bezahlbar.
 *
 * Beide sind rahmenwerk-frei (kein React), damit sie testbar bleiben; die
 * React-Anbindung macht TschaukoAgentRoot.
 */

import { VoiceAgentController } from '../VoiceAgentController'
import type { CommandRegistry } from '../types'
import type { AgentPersona } from './AgentPersona'
import { tenantPersonas } from './TscheppaPersonas'

export class AgentPersonaRegistry {
  private readonly byId = new Map<string, AgentPersona>()
  private readonly order: string[] = []

  constructor(personas: AgentPersona[]) {
    for (const persona of personas) this.register(persona)
  }

  register(persona: AgentPersona): this {
    if (!this.byId.has(persona.id)) this.order.push(persona.id)
    this.byId.set(persona.id, persona)
    return this
  }

  /** Figur per Kennung — unbekannt heißt: es gibt sie nicht (kein Raten). */
  get(id: string): AgentPersona | null {
    return this.byId.get(id) ?? null
  }

  /** Die Vorgabe-Figur des Mandanten (erste registrierte). */
  get default(): AgentPersona {
    const first = this.order[0]
    const persona = first ? this.byId.get(first) : undefined
    if (!persona) throw new Error('AgentPersonaRegistry: keine Figur registriert')
    return persona
  }

  list(): AgentPersona[] {
    return this.order.map(id => this.byId.get(id)!).filter(Boolean)
  }
}

export interface AgentSessionManagerDeps {
  registry: CommandRegistry
  onError?: (event: string, err: unknown, ctx?: Record<string, unknown>) => void
  onInfo?: (event: string, ctx?: Record<string, unknown>) => void
}

export class AgentSessionManager {
  private readonly controllers = new Map<string, VoiceAgentController>()
  private readonly personas: AgentPersonaRegistry
  private readonly deps: AgentSessionManagerDeps
  private activeId: string

  constructor(personas: AgentPersonaRegistry, deps: AgentSessionManagerDeps) {
    this.personas = personas
    this.deps = deps
    this.activeId = personas.default.id
  }

  /** Controller einer Figur — beim ersten Zugriff erzeugt und gemerkt. */
  controllerFor(personaId: string): VoiceAgentController {
    const persona = this.personas.get(personaId) ?? this.personas.default
    const existing = this.controllers.get(persona.id)
    if (existing) return existing
    const created = new VoiceAgentController(persona.toControllerConfig(this.deps))
    this.controllers.set(persona.id, created)
    return created
  }

  get activePersonaId(): string { return this.activeId }
  get activePersona(): AgentPersona { return this.personas.get(this.activeId) ?? this.personas.default }
  get active(): VoiceAgentController { return this.controllerFor(this.activeId) }

  /**
   * Figur wechseln. Eine laufende Sitzung der alten Figur wird beendet —
   * sonst spräche eine unsichtbare Stimme weiter und beide teilten sich
   * Mikrofon und Audio-Ausgabe.
   */
  activate(personaId: string): VoiceAgentController {
    if (personaId === this.activeId) return this.active
    const previous = this.controllers.get(this.activeId)
    if (previous) {
      const status = previous.getSnapshot().status
      if (status !== 'idle' && status !== 'closed' && status !== 'error') previous.close()
    }
    this.activeId = (this.personas.get(personaId) ?? this.personas.default).id
    return this.active
  }
}

/** Die Registry des aktiven Mandanten. */
export function createTenantPersonaRegistry(): AgentPersonaRegistry {
  return new AgentPersonaRegistry(tenantPersonas())
}
